import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Badge,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Science,
  Assignment,
  Timer,
  CheckCircle,
  Warning,
  Error,
  PlayArrow,
  Pause,
  Stop,
  Refresh,
  Add,
  Edit,
  Visibility,
  Schedule,
  Person,
  LocationOn,
  TrendingUp,
  Speed,
  Done,
  Analytics,
  AccessTime,
} from '@mui/icons-material';

// Import the laboratory access service
import { laboratoryAccessService, LaboratoryDashboardData } from '../../services/laboratoryAccessService';

export interface Sample {
  id: string;
  sample_id: string;
  client_name?: string;
  client?: { company_name: string };
  type_echantillon: string;
  statut: string;
  current_laboratory?: string;
  assigned_methods?: string[];
  selected_laboratory_methods?: string[];
  priority?: string;
  priorite?: string;
  date_reception: string;
  progress_percentage?: number;
}

export interface LaboratoryWorkflow {
  id: string;
  sample_id: string;
  laboratory: string;
  method_codes: string[];
  status: 'PENDING' | 'PREP_CHIMIQUE' | 'ANALYSE' | 'COMPLETED';
  assigned_to?: string;
  prep_technician?: string;
  analyst?: string;
  started_at?: string;
  prep_chimique_started_at?: string;
  prep_chimique_completed_at?: string;
  analyse_started_at?: string;
  analyse_completed_at?: string;
  completed_at?: string;
  progress_percentage: number;
  is_overdue: boolean;
}

export interface LaboratoryStats {
  total_samples: number;
  pending_samples: number;
  in_prep_chimique: number;
  in_analyse: number;
  completed_today: number;
  overdue_samples: number;
  average_turnaround_hours: number;
  efficiency_percentage: number;
}

interface LaboratoryDashboardProps {
  laboratoryType: 'SAA' | 'ICP' | 'AU' | 'VOLUMETRIE';
  laboratoryName: string;
  userRole: 'ANALYSTE' | 'TECHNICIEN' | 'SUPERVISEUR';
  userId: string;
}

const LaboratoryDashboard: React.FC<LaboratoryDashboardProps> = ({
  laboratoryType,
  laboratoryName,
  userRole,
  userId,
}) => {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [workflows, setWorkflows] = useState<LaboratoryWorkflow[]>([]);
  const [stats, setStats] = useState<LaboratoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'start_prep' | 'complete_prep' | 'start_analysis' | 'complete_analysis'>('start_prep');

  useEffect(() => {
    loadDashboardData();
    // Set up auto-refresh every 10 seconds (optimized for real-time updates)
    const interval = setInterval(loadDashboardData, 10000);
    return () => clearInterval(interval);
  }, [laboratoryType]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`🔄 Loading dashboard data for ${laboratoryType}...`);
      
      // Get real dashboard data from API
      const dashboardData = await laboratoryAccessService.getLaboratoryDashboard(`LABORATOIRE_${laboratoryType}`);
      
      console.log('📊 Dashboard data received:', dashboardData);
      
      // Transform samples to match expected format
      const transformedSamples: Sample[] = dashboardData.samples.map(sample => ({
        id: sample.id,
        sample_id: sample.sample_id,
        client_name: sample.client?.company_name || sample.client_name || 'Unknown Client',
        client: sample.client,
        type_echantillon: sample.type_echantillon,
        statut: sample.statut,
        current_laboratory: sample.current_laboratory,
        assigned_methods: sample.selected_laboratory_methods || sample.assigned_methods || [],
        selected_laboratory_methods: sample.selected_laboratory_methods,
        priority: sample.priority || sample.priorite,
        priorite: sample.priorite,
        date_reception: sample.date_reception,
        progress_percentage: sample.progress_percentage || 0,
      }));

      // Transform statistics
      const transformedStats: LaboratoryStats = {
        total_samples: dashboardData.statistics.totalSamples,
        pending_samples: dashboardData.statistics.pendingSamples,
        in_prep_chimique: dashboardData.statistics.inPrepChimique,
        in_analyse: dashboardData.statistics.inAnalyse,
        completed_today: dashboardData.statistics.completedToday,
        overdue_samples: dashboardData.statistics.overdueSamples,
        average_turnaround_hours: dashboardData.statistics.averageTurnaroundHours,
        efficiency_percentage: dashboardData.statistics.efficiencyPercentage,
      };

      setSamples(transformedSamples);
      setStats(transformedStats);
      
      console.log(`✅ Successfully loaded ${transformedSamples.length} samples for ${laboratoryType}`);
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      setError('Erreur lors du chargement des données du laboratoire. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAction = (sample: Sample, action: typeof actionType) => {
    setSelectedSample(sample);
    setActionType(action);
    setActionDialogOpen(true);
  };

  const handleCompleteAction = async () => {
    if (!selectedSample) return;

    try {
      // TODO: Implement actual API call to update workflow status
      console.log(`${actionType} for sample ${selectedSample.sample_id}`);
      
      setActionDialogOpen(false);
      setSelectedSample(null);
      loadDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error updating sample status:', error);
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (status.includes('PREP_CHIMIQUE')) return 'warning';
    if (status.includes('ANALYSE')) return 'info';
    if (status === 'COMPLETED') return 'success';
    return 'default';
  };

  const getPriorityColor = (priority: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (priority) {
      case 'URGENTE': return 'error';
      case 'ELEVEE': return 'warning';
      default: return 'default';
    }
  };

  const getActionButton = (sample: Sample) => {
    const status = sample.statut;
    
    if (status.includes('PREP_CHIMIQUE')) {
      if (userRole === 'ANALYSTE' || userRole === 'TECHNICIEN') {
        return (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<PlayArrow />}
            onClick={() => handleStartAction(sample, 'complete_prep')}
          >
            Terminer Prep Chimique
          </Button>
        );
      }
    } else if (status.includes('ANALYSE')) {
      if (userRole === 'ANALYSTE') {
        return (
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={() => handleStartAction(sample, 'complete_analysis')}
          >
            Terminer Analyse
          </Button>
        );
      }
    }
    
    return (
      <Button
        size="small"
        variant="outlined"
        startIcon={<Visibility />}
        onClick={() => setSelectedSample(sample)}
      >
        Voir Détails
      </Button>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {laboratoryName} - Chargement...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Science color="primary" />
          {laboratoryName}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadDashboardData}
          >
            Actualiser
          </Button>
          <Chip
            label={`Rôle: ${userRole}`}
            color="primary"
            icon={<Person />}
          />
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Échantillons
                </Typography>
                <Typography variant="h4">
                  {stats.total_samples}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  En Préparation Chimique
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.in_prep_chimique}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  En Analyse
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.in_analyse}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Terminés Aujourd'hui
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.completed_today}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {stats && stats.overdue_samples > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>{stats.overdue_samples}</strong> échantillon(s) en retard nécessitent une attention immédiate.
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label="Préparation Chimique" icon={<Badge badgeContent={stats?.in_prep_chimique} color="warning"><Assignment /></Badge>} />
          <Tab label="Analyse" icon={<Badge badgeContent={stats?.in_analyse} color="info"><Science /></Badge>} />
          <Tab label="Terminés" icon={<Badge badgeContent={stats?.completed_today} color="success"><CheckCircle /></Badge>} />
        </Tabs>
      </Box>

      {/* Sample Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID Échantillon</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Méthodes</TableCell>
                  <TableCell>Priorité</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Progrès</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {samples
                  .filter(sample => {
                    // Filter samples based on selected tab and laboratory type
                    const isLabSample = sample.current_laboratory === `LABORATOIRE_${laboratoryType}` ||
                                       sample.statut.includes(`LABORATOIRE_${laboratoryType}`) ||
                                       sample.statut.includes(laboratoryType);
                    
                    if (!isLabSample) return false;
                    
                    if (selectedTab === 0) {
                      // Préparation Chimique tab - show samples in prep phase
                      return sample.statut.includes('PREP_CHIMIQUE') && 
                             sample.statut.includes(laboratoryType);
                    }
                    if (selectedTab === 1) {
                      // Analyse tab - show samples in analysis phase
                      return sample.statut.includes('ANALYSE') && 
                             !sample.statut.includes('PREP_CHIMIQUE') &&
                             sample.statut.includes(laboratoryType);
                    }
                    if (selectedTab === 2) {
                      // Terminés tab - show completed samples
                      return sample.statut === 'VALIDATION_SERVICE' || 
                             sample.statut === 'GENERATION_CERTIFICAT' ||
                             sample.statut === 'PRET';
                    }
                    return true;
                  })
                  .map((sample) => (
                    <TableRow key={sample.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {sample.sample_id}
                        </Typography>
                      </TableCell>
                      <TableCell>{sample.client_name}</TableCell>
                      <TableCell>
                        <Chip label={sample.type_echantillon} size="small" />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {sample.assigned_methods?.slice(0, 2).map((method, index) => (
                            <Chip key={index} label={method} size="small" variant="outlined" />
                          ))}
                          {(sample.assigned_methods?.length || 0) > 2 && (
                            <Chip label={`+${(sample.assigned_methods?.length || 0) - 2}`} size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={sample.priority || sample.priorite || 'NORMALE'} 
                          color={
                            (sample.priority || sample.priorite) === 'URGENTE' ? 'error' : 
                            (sample.priority || sample.priorite) === 'ELEVEE' ? 'warning' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={sample.statut.replace('LABORATOIRE_', '').replace('_', ' ')}
                          size="small"
                          color={getStatusColor(sample.statut)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={sample.progress_percentage} 
                            sx={{ width: 60 }}
                          />
                          <Typography variant="caption">
                            {sample.progress_percentage}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getActionButton(sample)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'complete_prep' && 'Terminer Préparation Chimique'}
          {actionType === 'complete_analysis' && 'Terminer Analyse'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Échantillon: <strong>{selectedSample?.sample_id}</strong>
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes et Observations"
              placeholder="Ajoutez vos notes sur le processus..."
              sx={{ mt: 2 }}
            />

            {actionType === 'complete_analysis' && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Contrôle Qualité</InputLabel>
                <Select defaultValue="PASSED">
                  <MenuItem value="PASSED">Réussi</MenuItem>
                  <MenuItem value="FAILED">Échoué</MenuItem>
                  <MenuItem value="REQUIRES_RETEST">Nécessite un nouveau test</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleCompleteAction} variant="contained">
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LaboratoryDashboard; 